import React from 'react';
import { TripRequest, TripRequestMatchingNotesUpdate } from '@marageti/z4-sdk/lib/travel';
import { SubmitHandler, FieldValues } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useApiClient } from '@marageti/z4-lib';
import { MatchingNoteName } from '@marageti/z4-sdk/lib/standards';
import { useNavigate } from 'react-router-dom';
import EditNotesModalView from './edit-notes-modal-view';
import { excludedNotes, specialNotesOptions } from '../../../utils/trip-requests';

type EditNotesModalContainerProps = {
  open: boolean,
  onClose: () => void,
  tripRequest?: TripRequest,
  onSuccess: () => void,
};

const EditNotesModalContainer = ({
  open, onClose, tripRequest, onSuccess,
}: EditNotesModalContainerProps) => {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    try {
      if (tripRequest) {
        const {
          specialNotes, additionalComments,
        } = data;
        const tripRequestMatchingNotes: TripRequestMatchingNotesUpdate = {
          id: tripRequest.id,
          matchingNotes: {},
        };

        specialNotes.forEach((note: any) => {
          tripRequestMatchingNotes.matchingNotes[note.value as keyof typeof tripRequestMatchingNotes.matchingNotes] = { title: '', body: '' };
        });
        if (additionalComments) {
          tripRequestMatchingNotes.matchingNotes[MatchingNoteName.AdditionalComments] = { title: '', body: additionalComments };
        }

        await apiClient.tripRequestsClient.putTripRequestMatchingNotes(tripRequest.id, tripRequestMatchingNotes);
        enqueueSnackbar('Partner notes updated.', { variant: 'success' });
        onClose();
        onSuccess();
      }
    } catch (error: any) {
      if (error.code === 401) navigate('/logout');
      if (error.code === 404) navigate('/404', { state: { error } });
    }
  };

  const specialNotes = tripRequest?.matchingNotes ? Object.entries(tripRequest.matchingNotes)
    .filter(([key]) => !excludedNotes.includes(key as MatchingNoteName))
    .map(([value, matchingNote]) => ({ label: matchingNote.title, value })) : [];

  const handleClose = (event: {}, reason?: string) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  return (
    <EditNotesModalView
      submissionError={undefined}
      defaultValues={{
        specialNotes,
        additionalComments: tripRequest?.matchingNotes?.ADDITIONAL_COMMENTS?.body,
      }}
      specialNotesOptions={specialNotesOptions}
      tripRequest={tripRequest}
      open={open}
      onClose={handleClose}
      onSubmit={onSubmit}
    />
  );
};

export default EditNotesModalContainer;
